import { useCallback } from 'react';

import useMessengerRoomCreation, { CreateMessengerRoomResult } from 'hooks/roomCreation/useMessengerRoomCreation';
import useRoomAccessInitialState from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

export interface CreateMeetingGroupChatOptions {
  participantMxids: string[];
  name?: string;
}

export interface UseMeetingGroupChatCreationReturn {
  createMeetingGroupChat: (opts: CreateMeetingGroupChatOptions) => Promise<CreateMessengerRoomResult>;
}

/**
 * Returns `createMeetingGroupChat` — a creator for the meeting-room group chat (a messenger room) with
 * the workspace context + chat-scoped power levels pre-bound. Shared by `InstantCallModal` (via the
 * creator injected into `linkMeetingGroupChat`) and `useScheduleMeetingSubmit` (no-conflict branch).
 *
 * BUG-003: the group chat is a messenger room → chat-scoped power levels (mirrors the `/messenger`
 * path), not the meeting-scoped ones used for the meeting space + dedicated chat-child rooms.
 */
const useMeetingGroupChatCreation = (): UseMeetingGroupChatCreationReturn => {
  const { createMessengerRoom } = useMessengerRoomCreation();
  const { workspaceId } = useWorkspacesContext();
  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const { powerLevelOverrides: chatPowerLevelOverrides, ownerPowerLevel: chatOwnerPowerLevel } =
    useRoomCreationPowerLevels('chat');
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');

  const createMeetingGroupChat = useCallback(
    ({ participantMxids, name }: CreateMeetingGroupChatOptions): Promise<CreateMessengerRoomResult> =>
      createMessengerRoom({
        name,
        participantMxids,
        workspaceId,
        ...(workspaceId
          ? {
              roomManagersInitialState,
              powerLevelOverrides: chatPowerLevelOverrides,
              ownerPowerLevel: chatOwnerPowerLevel,
              roomAccessInitialState: teamRoomAccessInitialState,
            }
          : {}),
      }),
    [
      createMessengerRoom,
      workspaceId,
      roomManagersInitialState,
      chatPowerLevelOverrides,
      chatOwnerPowerLevel,
      teamRoomAccessInitialState,
    ]
  );

  return { createMeetingGroupChat };
};

export default useMeetingGroupChatCreation;
